import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import useTimer from '../../hooks/useTimer';
import { setActiveWorkshop } from '../../actions/app';
import { baseMoment, getEventTimeFormat, getLocaleDateFormat } from '../../helpers/dates';
import ActivityLocation from '../ActivityLocation';

const useStyles = makeStyles((theme) => ({
  buttonBox: {
    backgroundColor: 'transparent',
    width: '100%',
    overflow: 'hidden',
    border: 'none',
    textAlign: 'left',
    minWidth: 0,
    padding: 0,
  },
  card: {
    gap: theme.spacing(1.5),
    fontSize: 14,
    width: '100%',
    padding: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    cursor: 'pointer',
  },
  dates: {
    color: theme.palette.darkGrey.light,
    display: 'flex',
  },
  name: {
    fontSize: 14,
    fontWeight: 900,
    color: theme.palette.darkGrey.light,
  },
  onGoing: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 20,
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0, 1),
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: theme.palette.green.main,
    borderRadius: 30,
    color: theme.palette.background.default,
  },
  location: {
    color: theme.palette.darkGrey.light,
  },
}));

const TimestampCard = ({
  events,
  activity,
  setActiveWorkshop: dispatchSetActiveWorkshop,
  id, name, startDate, endDate,
}) => {
  const classes = useStyles();
  const timeFormat = getEventTimeFormat(events);
  const { locale } = useParams();
  const { t } = useTranslation('speakers');
  const dateFormat = getLocaleDateFormat('full', locale);
  const onClick = () => dispatchSetActiveWorkshop(id);
  const [inRange, setInRange] = useState(false);

  const onTick = useCallback((now, start, end) => {
    if (now.isBetween(start, end, null, '[)')) setInRange(true);
    else setInRange(false);
  }, [setInRange]);
  useTimer({
    startDate,
    endDate,
    onTick,
    step: 60000,
  });

  return (
    <>
      <button
        className={classes.buttonBox}
        onClick={onClick}
        type="button"
      >
        <div className={classes.card}>
          <Typography className={classes.name} component="div">
            {name}
          </Typography>

          <Typography className={classes.dates}>
            <span>{baseMoment(startDate).format(`${dateFormat}, ${timeFormat}`)} - {baseMoment(endDate).format(timeFormat)}</span>
            {!inRange && (
              <span className={classes.onGoing}>
                {t('activities.ongoing')}
              </span>
            )}
          </Typography>
          <ActivityLocation
            className={classes.location}
            activity={activity}
            multiline
          />
        </div>
      </button>
    </>
  );
};

TimestampCard.propTypes = {
  events: PropTypes.objectOf(
    CustomPropTypes.event
  ).isRequired,
  setActiveWorkshop: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  activity: PropTypes.shape({
    location: PropTypes.string,
    attendanceMode: PropTypes.string,
  }).isRequired,
};

TimestampCard.defaultProps = {
};

const mapStateToProps = ({
  entities: {
    events,
  },
}) => ({
  events,
});

export default connect(mapStateToProps, { setActiveWorkshop })(TimestampCard);
